// frontend/src/features/ogdb2/pages/HomeDashboard.jsx
import React, { useEffect, useState } from "react";
import { OGDB2 } from "../ogdb2Api";

import BAIDSearchPanel from "../components/BAIDSearchPanel";
import BAIDResultsTable from "../components/BAIDResultsTable";

const VIEW_KEY = "ogdb2_bulk_views_v3";

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readViewsLocal() {
  const raw = localStorage.getItem(VIEW_KEY);
  const arr = safeJsonParse(raw || "[]", []);
  return Array.isArray(arr) ? arr : [];
}

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d || "");
  }
}

export default function HomeDashboard({
  email,
  onGoAnalysis,
  onGoImport,
  onOpenBAID,
  onOpenAnalysisView,
}) {
  const yearNow = new Date().getFullYear();

  // ---------- BAID search state ----------
  const [baidClient, setBaidClient] = useState("");
  const [baidTaxYear, setBaidTaxYear] = useState(String(yearNow));

  const [rrcStatus, setRrcStatus] = useState("");
  const [rrcBusy, setRrcBusy] = useState(false);
  const [rrcModalOpen, setRrcModalOpen] = useState(false);
  const [rrcTaxYear, setRrcTaxYear] = useState(String(yearNow));
  const [rrcClient, setRrcClient] = useState("");
  const [rrcForceAll, setRrcForceAll] = useState(false);

  const [q, setQ] = useState("");
  const [rrc, setRrc] = useState("");
  const [leaseName, setLeaseName] = useState("");
  const [operator, setOperator] = useState("");
  const [county, setCounty] = useState("");
  const [externalAccountId, setExternalAccountId] = useState("");
  const [interestType, setInterestType] = useState("");

  const [limit, setLimit] = useState(200);
  const [sort, setSort] = useState("updated_at_desc");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // ---------- Saved views ----------
  const [viewsBusy, setViewsBusy] = useState(false);
  const [viewsErr, setViewsErr] = useState("");
  const [savedViews, setSavedViews] = useState(() => readViewsLocal());

  async function refreshViews() {
    // Always show local immediately
    setSavedViews(readViewsLocal());

    setViewsBusy(true);
    setViewsErr("");
    try {
      const res = await OGDB2.listSavedViews(); // /ogdb2/views :contentReference[oaicite:3]{index=3}
      const serverViews = Array.isArray(res?.data) ? res.data : [];

      // Normalize to the same shape BulkAnalysisPage expects :contentReference[oaicite:4]{index=4}
      const normalized = serverViews.map((v) => ({
        id: v.id,
        name: v.name,
        state: v.state || {},
        is_shared: !!v.is_shared,
        share_slug: v.share_slug,
        owner_email: v.owner_email,
        savedAt: v.updated_at || new Date().toISOString(),
      }));

      setSavedViews(normalized);

      // Also cache into localStorage so Home always has something
      try {
        localStorage.setItem(VIEW_KEY, JSON.stringify(normalized));
      } catch {}
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setViewsErr(
        status
          ? `Couldn’t load server views (${status}). Showing local cache only.`
          : `Couldn’t load server views. Showing local cache only.`
      );
      // keep local views displayed
    } finally {
      setViewsBusy(false);
    }
  }

  useEffect(() => {
    refreshViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runBAIDSearch() {
    const client = String(baidClient || "").trim();
    if (!client) {
      setErr("Client is required to search BAIDs.");
      return;
    }

    setBusy(true);
    setErr("");

    try {
      const ty = Number.isFinite(Number(baidTaxYear)) ? Number(baidTaxYear) : null;

      const filters = [{ type: "field", field: "client", op: "eq", value: client }];

      const addContains = (field, val) => {
        const s = String(val || "").trim();
        if (!s) return;
        filters.push({ type: "field", field, op: "contains", value: s });
      };

      addContains("rrc_number", rrc);
      addContains("lease_name", leaseName);
      addContains("operator", operator);
      addContains("county", county);
      addContains("external_account_id", externalAccountId);

      const it = String(interestType || "").trim();
      if (it) filters.push({ type: "field", field: "interest_type", op: "eq", value: it });

      const payload = {
        search: {
          q: String(q || "").trim() || null,
          limit: Number(limit) || 200,
          offset: 0,
          sort,
          filters,
        },
        metric_codes: [],
        tax_year: ty,
      };

      const res = await OGDB2.bulkQueryBAID(payload); // /ogdb2/baid/bulk/query :contentReference[oaicite:5]{index=5}
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setRows(items);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "BAID search failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">OGDB2 Dashboard</div>
            <div className="text-xs text-gray-500">BAID-first home.</div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button className="btn" type="button" onClick={onGoImport}>
              Import Wizard
            </button>
            <button className="btn" type="button" onClick={onGoAnalysis}>
              Analysis
            </button>
            <button
              className="btn"
              type="button"
              disabled={rrcBusy}
              onClick={() => {
                setRrcClient(baidClient.trim());
                setRrcTaxYear(String(baidTaxYear || yearNow));
                setRrcStatus("");
                setRrcModalOpen(true);
              }}
              title="Fetch monthly production from Texas RRC for BAIDs missing data"
            >
              {rrcBusy ? (
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3.5 h-3.5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                  Fetching RRC...
                </span>
              ) : "Refresh RRC Data"}
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{String(err)}</div>
      ) : null}

      {/* RRC Refresh Modal */}
      {rrcModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-[min(480px,92vw)] overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">Refresh RRC Production Data</div>
              <button className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setRrcModalOpen(false)}>
                Close
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="text-xs text-gray-600">
                Fetches monthly production from the Texas Railroad Commission for BAIDs that are missing data.
                Queries the RRC per-lease (works on Render).
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700">
                  Client <span className="text-red-600">*</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                    value={rrcClient}
                    onChange={(e) => setRrcClient(e.target.value)}
                    placeholder="e.g. Eagle Ridge"
                    disabled={rrcBusy}
                  />
                </label>
                <label className="text-xs text-gray-700">
                  Tax Year <span className="text-red-600">*</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                    value={rrcTaxYear}
                    onChange={(e) => setRrcTaxYear(e.target.value)}
                    placeholder="e.g. 2026"
                    disabled={rrcBusy}
                  />
                  <div className="text-[11px] text-gray-500 mt-1">
                    Tax year 2026 fetches 2025 production data.
                  </div>
                </label>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={rrcForceAll}
                  onChange={(e) => setRrcForceAll(e.target.checked)}
                  disabled={rrcBusy}
                />
                <span>Pull for all BAIDs (overwrite existing data)</span>
              </div>

              {rrcStatus && (
                <div className={`rounded-xl p-3 text-xs flex items-start gap-2 ${
                  rrcBusy
                    ? "border border-blue-200 bg-blue-50 text-blue-800"
                    : rrcStatus.includes("failed") || rrcStatus.includes("Error")
                    ? "border border-red-200 bg-red-50 text-red-700"
                    : "border border-green-200 bg-green-50 text-green-800"
                }`}>
                  {rrcBusy && (
                    <span className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0 mt-0.5" />
                  )}
                  <span>{rrcStatus}</span>
                </div>
              )}

              {!rrcBusy && (
                <div className="text-[11px] text-gray-500">
                  Safe to close this popup while running. The button will keep spinning until complete.
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  className="btn"
                  disabled={rrcBusy || !rrcClient.trim() || !rrcTaxYear}
                  onClick={async () => {
                    const client = rrcClient.trim();
                    const ty = Number(rrcTaxYear);
                    if (!client || !ty) return;
                    setRrcBusy(true);
                    setRrcStatus("Querying RRC for production data...");
                    try {
                      const res = await OGDB2.refreshRRCProduction(client, ty, { forceAll: rrcForceAll });
                      const d = res?.data || {};
                      if (d.rrc_matches_found === 0 && d.baids_queried === 0) {
                        setRrcStatus("All BAIDs already have production data for this year.");
                      } else {
                        setRrcStatus(
                          `Done! ${d.rrc_matches_found} wells matched out of ${d.baids_queried} queried. ` +
                          `Stored ${d.stored_monthly} monthly rows + ${d.stored_facts} start rate facts.`
                        );
                      }
                    } catch (e) {
                      setRrcStatus("Error: " + (e?.response?.data?.detail || e?.message || "RRC refresh failed"));
                    } finally {
                      setRrcBusy(false);
                    }
                  }}
                >
                  {rrcBusy ? "Running..." : "Fetch Production Data"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent views */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-gray-800">Recent Analysis Views</div>
          <div className="flex items-center gap-2">
            <button className="btn" type="button" onClick={refreshViews} disabled={viewsBusy}>
              Refresh
            </button>
            <button className="btn" type="button" onClick={onGoAnalysis}>
              Open Analysis
            </button>
          </div>
        </div>

        {viewsErr ? (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            {viewsErr}
          </div>
        ) : null}

        {viewsBusy ? (
          <div className="text-xs text-gray-500 mt-2">Loading…</div>
        ) : savedViews.length ? (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Owner</th>
                  <th className="py-2 pr-3">Shared</th>
                  <th className="py-2 pr-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {savedViews.slice(0, 10).map((v, idx) => (
                  <tr
				    key={v?.id ?? `${v?.name ?? "view"}_${idx}`}
				    className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
				    onClick={() => onOpenAnalysisView?.(v)}
				    title="Open this view in Analysis"
				  >
				    <td className="py-2 pr-3 font-medium text-gray-900 underline underline-offset-2">
					  {v?.name || "Untitled"}
				    </td>
				    <td className="py-2 pr-3 text-gray-600">{v?.owner_email || "local"}</td>
				    <td className="py-2 pr-3 text-gray-600">{v?.is_shared ? "yes" : "no"}</td>
				    <td className="py-2 pr-3 text-gray-600">{fmtDate(v?.savedAt || v?.updated_at || v?.created_at)}</td>
				  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-[11px] text-gray-500">
              Source: local cache key <code>{VIEW_KEY}</code> (and server <code>/ogdb2/views</code> if available).
            </div>
          </div>
        ) : (
          <div className="text-xs text-gray-500 mt-2">
            No views found yet. (Once you save views inside Analysis, they’ll show up here.)
          </div>
        )}
      </div>

      {/* BAID search */}
      <BAIDSearchPanel
        email={email}
        busy={busy}
        client={baidClient}
        setClient={setBaidClient}
        taxYear={baidTaxYear}
        setTaxYear={setBaidTaxYear}
        q={q}
        setQ={setQ}
        rrc={rrc}
        setRrc={setRrc}
        leaseName={leaseName}
        setLeaseName={setLeaseName}
        operator={operator}
        setOperator={setOperator}
        county={county}
        setCounty={setCounty}
        externalAccountId={externalAccountId}
        setExternalAccountId={setExternalAccountId}
        interestType={interestType}
        setInterestType={setInterestType}
        limit={limit}
        setLimit={setLimit}
        sort={sort}
        setSort={setSort}
        onSearch={runBAIDSearch}
      />

      <BAIDResultsTable busy={busy} rows={rows} onOpen={(unit) => onOpenBAID?.(unit, baidTaxYear)} />
    </div>
  );
}
